import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BrandColors } from '@hallmark/web.styles.colors';
import { useAnalyticsContext } from '../../../context/analytics-context';
import { setIsImageUploadToPhotoZoneDrawerOpen, setIsWamDrawerOpen, useAppContext } from '../../../context/app-context';
import { setSelectedPhotoZone, useCardContext } from '../../../context/card-context';
import { useInitializationDataContext } from '../../../context/data-context';
import { useFeatureFlags } from '../../../hooks';
import { ActionType, useDatadog } from '../../../hooks/use-datadog';
import colorsList from '../../../styles/util.scss';
import {
  addUserTextHandlers,
  CanvasDataTypes,
  convertPointToPixel,
  getFontidByUrlName,
  handlePhototextzoneSelection,
} from '../../../utils';
import { getCardFaceClipPath } from '../../../utils/canvas-utils/get-card-face-clip-path';
import { parseTextAction } from '../../../utils/parse-text-action';
import { getTextSettings, removeEditableAreaButtons } from '../../card-editor/utils';
import { ResetTextData } from '../card-face-types';
import { createTextElement } from '../utils/create-text-element';
import { usePhotoTextZoneButtons } from './use-photo-text-zone-buttons';

export const useCanvasMouseUp = ({ cardIndex, fabric, editingHybridBox, setIsResetTextModalOpen }) => {
  const { initializedDataState } = useInitializationDataContext();
  const { cardState, cardDispatch } = useCardContext();
  const { appDispatch } = useAppContext();
  const { trackPhotoUploadMethod } = useAnalyticsContext();
  const { t } = useTranslation();
  const { handleTypeAMessage, handleAddHandwriting } = usePhotoTextZoneButtons();
  const { DISPLAY_ZOOM_DEV, SAS_DYNAMIC_BUTTONS } = useFeatureFlags();
  const { addAction, textAction } = useDatadog();
  const [resetTextData, setResetTextData] = useState<ResetTextData>({
    canvas: null,
    zoneName: '',
  });
  const { isUK } = initializedDataState;
  const defaultTextAreaSettings = { ...initializedDataState.data?.variables.template_data.DefaultTextArea };

  const handleMouseUp = ({ options, canvasRef, canvas }) => {
    //  handles the uploading image for a photo zone
    if (options?.target?.data?.type === CanvasDataTypes.PhotoZoneButton) {
      setSelectedPhotoZone(cardDispatch, options.target.data.photoZoneId);
      setIsImageUploadToPhotoZoneDrawerOpen(appDispatch);
      trackPhotoUploadMethod('camera icon');
    } else if (options?.target?.data?.type === CanvasDataTypes.UserZoneAddTextButton) {
      // Adds an editable text box to the user zone
      if (canvas && canvasRef.current) {
        const INITIAL_TEXT_MESSAGE = `${t('cardEditor.userTextDefaultMessage')}`;
        const cardFaceClipPath = getCardFaceClipPath(cardState.cardFacesList[`${cardIndex}`], 0);
        const defaultFallbackColor = isUK
          ? colorsList[BrandColors.CharcoalGrey]
          : initializedDataState.defaultColor || colorsList[BrandColors.Black];
        const defaultColor = defaultTextAreaSettings?.TextColor || defaultFallbackColor;
        const defaultFontSize = convertPointToPixel(
          initializedDataState.data?.variables.template_data.DefaultTextArea.FontSize || 50,
        );
        defaultTextAreaSettings.FontFamilyId = getFontidByUrlName(
          defaultTextAreaSettings?.FontFamily || '',
          initializedDataState.data?.font_collection.fonts,
        );

        const defaultTextSettings = getTextSettings({
          cardState,
          color: defaultColor,
          defaultFontSize,
          isUK,
          defaultOptions: defaultTextAreaSettings,
          name: CanvasDataTypes.UserText,
        });
        const width = cardFaceClipPath.width;

        const initialTextMessage = defaultTextSettings.Text || INITIAL_TEXT_MESSAGE;
        const textElement = createTextElement({
          width,
          fabric,
          initialTextMessage,
          isUK,
          defaultTextSettings,
          cardFaceClipPath,
          cardState,
        });

        addUserTextHandlers(textElement, initialTextMessage, defaultColor, canvasRef.current);
        textAction(ActionType.ADD_TEXT, parseTextAction(textElement));
        canvasRef.current.add(textElement);
        canvasRef.current.setActiveObject(textElement);
      }
      removeEditableAreaButtons(canvas);
    } else if (options?.target?.data?.type === CanvasDataTypes.UserZoneAddWamButton) {
      setIsWamDrawerOpen(appDispatch);
    } else if (options?.target?.data?.type === CanvasDataTypes.PhotoTextZoneTextButton) {
      editingHybridBox.current = true;
      handleTypeAMessage(canvas, canvasRef, options?.target, DISPLAY_ZOOM_DEV);
    } else if (options?.target?.data?.type === CanvasDataTypes.PhotoTextZoneWAMButton) {
      handleAddHandwriting(canvas, canvasRef, options?.target);
    } else if (options?.target?.data?.type === CanvasDataTypes.PhotoTextZoneResetButton) {
      if (canvas) {
        setIsResetTextModalOpen(true);
        setResetTextData({
          canvas: canvas,
          zoneName: options?.target?.data?.linkedZoneName,
        });
      }
    } else if (options?.target?.data?.type === CanvasDataTypes.PhotoTextZone) {
      const photoTextZone = options.target as fabric.Group;
      handlePhototextzoneSelection(photoTextZone, canvas, DISPLAY_ZOOM_DEV, SAS_DYNAMIC_BUTTONS);
    } else if (
      options?.target?.data?.type === CanvasDataTypes.PhotoZoneImage ||
      options?.target?.data?.type === CanvasDataTypes.UserImage
    ) {
      addAction('select-image', {
        image: options.target.data,
        cardFace: cardState.cardFacesList[`${cardState.activeCardIndex}`]?.type ?? null,
        zoneId: options.target.data.photoZoneId || null,
      });
    }
  };

  return { resetTextData, handleMouseUp };
};
